import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import type { TablesUpdate } from '../../types/database.types';
import {
  getCollaboratorsWithCategories,
  getCollaboratorById,
  updateCollaborator,
  setCollaboratorCategories,
  deactivateCollaborator,
  activateCollaborator,
  inviteCollaborator,
  resendCollaboratorInvitation,
  type InviteCollaboratorInput,
} from '../../services/collaborators/collaboratorService';

const collaboratorsKey = ['collaborators'] as const;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useCollaborators() {
  return useQuery({
    ...STATIC,
    queryKey: collaboratorsKey,
    queryFn: () => getCollaboratorsWithCategories(),
  });
}

export function useCollaboratorById(id: string | null) {
  return useQuery({
    ...STATIC,
    queryKey: [...collaboratorsKey, id],
    queryFn: () => (id ? getCollaboratorById(id) : null),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useInviteCollaborator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteCollaboratorInput) => inviteCollaborator(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: collaboratorsKey }),
  });
}

export function useUpdateCollaborator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & TablesUpdate<'collaborators'>) =>
      updateCollaborator(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: collaboratorsKey }),
  });
}

export function useSetCollaboratorCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collaboratorId,
      categoryIds,
    }: {
      collaboratorId: string;
      categoryIds: string[];
    }) => setCollaboratorCategories(collaboratorId, categoryIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: collaboratorsKey }),
  });
}

export function useDeactivateCollaborator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deactivateCollaborator(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: collaboratorsKey }),
  });
}

export function useActivateCollaborator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => activateCollaborator(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: collaboratorsKey }),
  });
}

export function useResendCollaboratorInvitation() {
  return useMutation({
    mutationFn: (collaboratorId: string) => resendCollaboratorInvitation(collaboratorId),
  });
}
